import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { setupGlobalErrorReporting } from './lib/errorReporter';
import { initSentry } from './lib/sentry';
setupGlobalErrorReporting();
// Auto-reload once if a route-split chunk fails to load. Happens
// immediately after every deploy: the user's tab has the old index.html
// in memory pointing at chunks the new build renamed. Without this, the
// affected panel just shows 'failed to fetch dynamically imported module'
// forever until the user manually hard-refreshes. We guard with a
// sessionStorage flag so we don't get into a reload loop if the server is
// genuinely broken.
(function setupChunkReload() {
  const FLAG = 'telos:chunk-reload-attempted';
  function handler(reason: unknown) {
    let msg = '';
    if (typeof reason === 'string') msg = reason;
    else if (reason && typeof reason === 'object' && 'message' in reason) {
      const candidate = (reason as { message?: unknown }).message;
      if (typeof candidate === 'string') msg = candidate;
    }
    if (!/Failed to fetch dynamically imported module|Loading chunk|Loading CSS chunk/i.test(msg)) return;
    if (sessionStorage.getItem(FLAG) === '1') return;
    sessionStorage.setItem(FLAG, '1');
    // eslint-disable-next-line no-console
    console.warn('[chunk-reload] stale chunk reference detected, reloading once:', msg);
    window.location.reload();
  }
  window.addEventListener('unhandledrejection', (e) => handler(e.reason));
  window.addEventListener('error', (e) => handler(e.message));
})();

initSentry();
import { AuthProvider } from '@/contexts/AuthContext';
import { TourProvider } from '@/contexts/TourContext';
import { SidebarProvider } from '@/contexts/SidebarContext';
import { queryClient } from '@/lib/queryClient';
import './index.css';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SidebarProvider>
            <TourProvider>
              <App />
            </TourProvider>
          </SidebarProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
